import { useMemo } from 'react';
import { useIsWebsiteV2 } from '@shared/hooks/websiteVersion';
import { useWebsiteV2GuestSitePublishedQuery } from './useWebsiteV2GuestGate.graphql.generated';
import { isJoyOwnedGuestPage } from './useWebsiteV2GuestGate.utils';

export type WebsiteV2GuestGate = Readonly<{
  /** The event carries `useWebsiteV2`: its guest site is owned by carbon, never by charm. */
  isWebsiteV2: boolean;
  /** True until the flag has resolved. */
  isLoading: boolean;
}>;

export type WebsiteV2GuestGatePage = Readonly<{
  eventHandle: string | null;
  pageSlug: string | undefined;
  isUrlBasedCustomPageSlug: boolean;
}>;

export type WebsiteV2GuestSitePublished = Readonly<{
  /** The URL is one of Joy's own guest flows (`/:handle/rsvp`, …), which the server never proxies to carbon. */
  isJoyOwnedPage: boolean;
  /** The carbon site is published. Only read on a Joy-owned page of a V2 event; false elsewhere. */
  isWebsiteV2Published: boolean;
  /** True until the published state has resolved, on a page that reads it. */
  isLoading: boolean;
}>;

/**
 * Guest-side half of the "flag on ⇒ charm never shows site content" rule (doc/website-v2/PLAN.md).
 *
 * For a `useWebsiteV2` event the server proxies the carbon document whenever a published carbon
 * site exists (or, for a signed-in event admin, the draft), and answers 503 when that proxy fails.
 * So the only way charm is rendering a flagged event outside a preview is that the server found
 * nothing this viewer may see — which makes "flagged and
 * charm is here" the whole answer, with no round trip: the site is unpublished, and charm must
 * show its "Site Under Construction" gate — even to admins, and regardless of the V1
 * `websiteState` — because the V1 content the couple left behind must not leak out through the
 * old renderer.
 *
 * Deliberately NOT a carbon lookup (removed 2026-09-09): a query could be defeated by a gateway
 * hiccup and cost every unpublished-site guest a request, while the inference cannot fail open.
 * When event_service exposes the event's active site on the `event` graph object, the server's
 * `resolveCarbonGuestSite` is the single swap point; this hook does not change.
 *
 * Except on Joy's own guest flows (`isJoyOwnedPath`, e.g. a new-stack wedding's `/:handle/rsvp`):
 * the server serves those itself, published site or not, so there the inference doesn't hold —
 * see `useWebsiteV2GuestSitePublished`.
 */
export const useWebsiteV2GuestGate = (): WebsiteV2GuestGate => {
  const { isWebsiteV2, isLoading } = useIsWebsiteV2();
  return useMemo(() => ({ isWebsiteV2: !isLoading && isWebsiteV2, isLoading }), [isWebsiteV2, isLoading]);
};

/**
 * The carbon site's published state, for the guest site's Joy-owned flows on a V2 event only: those
 * reach charm whatever the site's state, so it is read from `Event.activeWebsiteV2Site` rather than
 * inferred. Kept apart from `useWebsiteV2GuestGate` so the flag-only callers (`useIsGuestSiteLive`)
 * don't need an Apollo client.
 */
export const useWebsiteV2GuestSitePublished = (gate: WebsiteV2GuestGate, page: WebsiteV2GuestGatePage): WebsiteV2GuestSitePublished => {
  const isJoyOwnedPage = isJoyOwnedGuestPage(page.pageSlug, page.isUrlBasedCustomPageSlug);
  const skip = gate.isLoading || !gate.isWebsiteV2 || !isJoyOwnedPage || !page.eventHandle;
  const { data, loading } = useWebsiteV2GuestSitePublishedQuery({
    variables: { eventHandle: page.eventHandle ?? '' },
    batchMode: 'fast',
    skip,
  });
  const isWebsiteV2Published = !skip && data?.eventByName?.activeWebsiteV2Site?.published === true;
  const isLoading = !skip && loading && !data;
  return useMemo(() => ({ isJoyOwnedPage, isWebsiteV2Published, isLoading }), [isJoyOwnedPage, isWebsiteV2Published, isLoading]);
};
